import React, { memo, useCallback, useState } from 'react';
import { createUseStyles, Input, Password, Text, Link, Button, Advice } from '@Sage/hoi-poi-ui';
import Cookies from 'js-cookie';

import { useWeb5Context } from 'lib/Web5Context';
import { getUserLoginRealm } from 'lib/services/auth';
import useLoginLiterals from 'lib/useLoginLiterals';

import { FadeTransition } from 'components/Transitions';

import AuthBox from '../components/AuthBox';

import styles from './styles.js';
const useStyles = createUseStyles(styles, 'Login');

const Login = ({
    logo,
    onLogin,
    onAuthType,
    children,
    iframeURL,
    signupURL,
    recoverPasswordURL,
}) => {
    const { platform } = useWeb5Context();
    const classes = useStyles();

    const [username, setUsername] = useState(Cookies.get('username'));
    const [password, setPassword] = useState();
    const [error, setError] = useState();
    const [realm, setRealm] = useState();
    const [loginLoading, setLoginLoading] = useState();

    const { loading, t } = useLoginLiterals();

    const cleanRealm = useCallback(() => {
        setRealm(null);
    }, []);

    const nextStep = useCallback(
        (e) => {
            e?.preventDefault?.();

            const finalUsername = username?.trim();
            const finalPassword = password?.trim();

            if (loginLoading || !finalUsername) return;

            setLoginLoading(true);

            // Checking auth mode
            if (!realm) {
                const platformInt = parseInt(platform, 10);

                getUserLoginRealm({
                    email: finalUsername,
                    intDeviceType: platformInt,
                    strClientVersion: platformInt === 101 ? 'WEB' : 'ADMIN',
                })
                    .then(({ data }) => {
                        const parsedData = JSON.parse(data);
                        const result = parsedData?.Result;
                        const authenticationType = result?.authenticationType;
                        if (authenticationType >= 4) {
                            return onAuthType && onAuthType(result);
                        } else if (authenticationType === -1) {
                            setRealm(authenticationType);
                        }
                    })
                    .catch((e) => {
                        console.error(e);
                        setError(t('error_generalerror'));
                    })
                    .finally(() => {
                        setLoginLoading(false);
                    });
            } else {
                onLogin(finalUsername, finalPassword)
                    .then(() => {
                        Cookies.set('username', finalUsername);
                    })
                    .catch((e) => {
                        let error = 'error_generalerror';
                        if (typeof e === 'string') error = e;
                        setError(t(error));
                    })
                    .finally(() => {
                        setLoginLoading(false);
                    });
            }

            return false;
        },
        [loginLoading, onAuthType, onLogin, password, platform, realm, t, username],
    );

    if (loading) return null;

    return (
        <>
            <AuthBox
                title={t('title_welcome_forcemanager_login')}
                logo={logo}
                iframeURL={iframeURL ? t(iframeURL) : null}
            >
                <FadeTransition transitionKey="error" show={error}>
                    <Advice
                        className={classes.error}
                        type="error"
                        showIcon
                        defaultCollapsed={false}
                        showCollapse={false}
                    >
                        {error}
                    </Advice>
                </FadeTransition>
                <form onSubmit={nextStep}>
                    <Input
                        label={t('label_email_or_mobile_login')}
                        placeholder={t('label_email_or_mobile_login')}
                        type="text"
                        id="username"
                        name="username"
                        autoComplete="username"
                        onFocus={cleanRealm}
                        onChange={setUsername}
                        value={username}
                        isFullWidth
                        isDisabled={loginLoading}
                        autoFocus
                    />
                    <FadeTransition transitionKey="password" show={!!realm}>
                        <Password
                            label={t('label_password')}
                            id="password"
                            name="password"
                            autoComplete="current-password"
                            isFullWidth
                            onChange={setPassword}
                            onEnter={nextStep}
                            value={password}
                            isDisabled={loginLoading}
                            autoFocus
                        />
                    </FadeTransition>
                    <div className={classes.forgot}>
                        <Link href={recoverPasswordURL} type="caption">
                            {t('action_question_forgot_password')}
                        </Link>
                    </div>
                    <div className={classes.footer}>
                        <Button onClick={nextStep} isLoading={loginLoading}>
                            {t('action_next')}
                        </Button>
                        {signupURL && (
                            <div className={classes.signup}>
                                <Text>{t('helptext_are_you_still_not_a_member')} </Text>
                                <Link href={signupURL} target="_blank">
                                    {t('action_start_free_trial_login')}
                                </Link>
                            </div>
                        )}
                    </div>
                </form>
            </AuthBox>
        </>
    );
};

export default memo(Login);
